import {
  FaqCategoryListResponse,
  FaqDetailResponse,
  FaqViewerState,
} from '../../interfaces/wiki/Faq';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper to build headers depending on auth state
const getHeaders = (authToken?: string | null) => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return headers;
};

export async function getPublishedFaqs(
  authToken?: string | null
): Promise<FaqCategoryListResponse[] | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/help/faqs`, {
      method: 'GET',
      headers: getHeaders(authToken),
    });

    if (!response.ok) {
      console.error('Failed to fetch published FAQs:', response.status, response.statusText);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching published FAQs:', error);
    return null;
  }
}

export async function getFaqBySlug(
  slug: string,
  authToken?: string | null
): Promise<FaqDetailResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/help/faqs/${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers: getHeaders(authToken),
    });

    if (!response.ok) {
      // 404 is expected if the slug doesn't exist or isn't published
      if (response.status !== 404) {
          console.error(`Failed to fetch FAQ ${slug}:`, response.status, response.statusText);
      }
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching FAQ ${slug}:`, error);
    return null;
  }
} 

export async function getPublishedFaqBySlug(
  authToken: string | null,
  slug: string
): Promise<FaqDetailResponse | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/help/faqs/${encodeURIComponent(slug)}`, {
      headers: getHeaders(authToken),
      cache: 'no-store'
    });

    if (!response.ok) return null;

    const data = await response.json();
    
    // Parse the stringified JSON from the database back into an object for Tiptap
    if (data && typeof data.content === 'string') {
      try {
        data.content = JSON.parse(data.content);
      } catch (e) {
        console.error("Failed to parse FAQ content JSON", e);
      }
    }

    return data;
  } catch (error) {
    console.error("Error fetching FAQ by slug:", error);
    return null;
  }
}

export interface FaqIndexItem {
  title: string;
  slug: string;
  summary: string;
}

export async function getFaqIndex(
  authToken: string
): Promise<FaqIndexItem[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/help/faqs`, {
      headers: getHeaders(authToken),
      cache: 'no-store'
    });

    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error("Error fetching FAQ index:", error);
    return [];
  }
}

export async function updateFaqReaction(
  authToken: string,
  pageId: string,
  helpful: boolean
): Promise<FaqViewerState | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/help/faqs/${pageId}/reaction`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ helpful }),
    });

    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Error saving FAQ reaction:', error);
    return null;
  }
}

// Side-effect-free title resolution, limited by the server to persisted references on this source.
export async function getFaqReferences(sourceId: string, token?: string | null, adminPreview = false): Promise<import('../../components/components/wiki/FaqReference').FaqReferenceResult[]> {
  const route = adminPreview ? `/admin/faqs/${encodeURIComponent(sourceId)}/references` : `/help/faqs/${encodeURIComponent(sourceId)}/references`;
  const response = await fetch(`${API_BASE_URL}${route}`, { headers: getHeaders(token), cache: 'no-store' });
  if (!response.ok) return [];
  return response.json();
}
export async function getPublishedFaqById(token: string | null, id: string): Promise<FaqDetailResponse | null> {
  const response = await fetch(`${API_BASE_URL}/help/faqs/by-id/${encodeURIComponent(id)}`, { headers: getHeaders(token), cache: 'no-store' });
  return response.ok ? response.json() : null;
}
